import { Anchor, Box, Divider, Flex, List } from '@mantine/core'

import { Text } from '@/components/Text/Text'
import { Title } from '@/components/Title/Title'

import classes from './PrivacyPolicy.module.css'

export function Component() {
  return (
    <Box className={classes.container}>
      <Flex className={classes.header}>
        <Title variant="h1">Privacy Policy for Rolan Wealth</Title>

        <Text variant="body_1_grey">
          Effective Date: July 7th, 2026 &nbsp;|&nbsp; Last Updated: July 7th,
          2026
        </Text>
      </Flex>

      <Title variant="h2">Summary of Key Points</Title>

      <Text>
        This summary is provided for convenience. The full legal terms follow
        below and govern in all cases.
      </Text>

      <List
        classNames={{
          itemWrapper: classes.listItem,
        }}
        mt={8}
        withPadding
      >
        <List.Item>
          <Text>
            We collect your name, email address, and the financial information
            you choose to enter &mdash; like your salary, expenses, and account
            balances &mdash; to power the Platform.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            When you connect a financial account through Plaid, we do not store
            your bank login credentials, account balances, or transaction
            history. We store only your account name, institution, last four
            digits of the account number, and a secure access token &mdash;
            nothing more.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            If you add an account manually (for example, entering your home
            value), we store the balance and account details you enter. All
            manually entered data is encrypted at rest.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            We never share your individual financial data, account activity, or
            personally identifiable information with your employer. Your
            employer may receive only high-level, anonymized reports about
            overall program engagement &mdash; never anything traceable to you.
          </Text>
        </List.Item>

        <List.Item>
          <Text>We do not sell your personal information. Ever.</Text>
        </List.Item>

        <List.Item>
          <Text>
            If you delete your account, your data is deleted immediately from
            our active systems. You are in control.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            We use cookies to keep the Platform running. Transactional emails we
            send may also contain tracking pixels that record whether the email
            was opened. We do not currently have a cookie consent banner; we are
            working to implement one. You can manage cookies through your
            browser settings, and you can disable email tracking by turning off
            automatic image loading in your email client.
          </Text>
        </List.Item>
      </List>

      <Title mt={20} variant="h2">
        Introduction
      </Title>

      <Text>
        CLZ Ventures LLC, a New York limited liability company doing business as
        Rolan Wealth (&ldquo;Rolan Wealth,&rdquo; &ldquo;we,&rdquo;
        &ldquo;us,&rdquo; or &ldquo;our&rdquo;), provides an AI-powered
        financial wellness platform supported by Certified Financial
        Planner&trade; (CFP®) professionals, designed to help employees achieve
        their financial goals. The Platform is made available to employees as a
        benefit sponsored by their employer (&ldquo;Platform&rdquo; or
        &ldquo;Services&rdquo;).
      </Text>

      <Text mt={8}>
        This Privacy Policy explains how we collect, use, disclose, retain, and
        protect personal information about individuals who use the Services,
        including employees of our employer clients (&ldquo;Employees&rdquo; or
        &ldquo;you&rdquo;). Please read this Privacy Policy carefully.
      </Text>

      <Text mt={8}>
        By accessing or using the Services, you acknowledge that you have read,
        understood, and agree to the collection, use, and disclosure of your
        personal information as described in this Privacy Policy. If you do not
        agree, please discontinue use of the Services immediately.{' '}
        <strong>
          This Privacy Policy does not govern the employment relationship
          between you and your employer.
        </strong>
      </Text>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">1. Information We Collect</Title>

      <Text>
        We collect personal information from and about you in the categories
        described below. &ldquo;Personal information&rdquo; means any
        information that identifies, relates to, describes, or could reasonably
        be linked to you as an individual.
      </Text>

      <Title mt={20} variant="h3">
        A. Information You Provide Directly
      </Title>

      <Text>When you register for or use the Services, you may provide:</Text>

      <List
        classNames={{
          itemWrapper: classes.listItem,
        }}
        mt={8}
        withPadding
      >
        <List.Item>
          <Text>
            <strong>Identification Information:</strong> Full name, work email
            address (used as your primary account identifier), and account
            credentials (username and password). Through your Profile settings,
            you may optionally provide a personal email address and a phone
            number. These fields are not required to use the Platform and are
            collected solely if you choose to provide them.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>
              Financial Information &mdash; Plaid-Connected Accounts:
            </strong>{' '}
            Where you connect a financial account through Plaid, Inc., we store
            your account name, institution name, and the last four digits of
            your account number as account-level identifiers. We also store an
            encrypted Plaid access token to authorize ongoing data retrieval.
            Account balances and transaction data are retrieved on-demand from
            Plaid and displayed on the Platform but are not independently
            stored, with one limited exception: where you modify a transaction
            (e.g., recategorizing, renaming, or excluding it), we store only the
            Plaid-issued transaction ID and your modification for reference.
            Underlying transaction details are not stored. We do not store your
            bank login credentials. Please see Section 1(C) for further detail
            on Plaid data practices.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>
              Financial Information &mdash; Manually Added Accounts:
            </strong>{' '}
            Where Plaid does not support a financial institution or account
            type, you may add accounts manually. For manually added accounts, we
            store the institution name, account name, account type, current
            balance, balance date, initial value, initial value date, estimation
            method, and any notes you choose to enter. All manually entered
            account data is encrypted at rest using AES-256 encryption. If a
            database breach were to occur, this data cannot be decoded without
            the encryption key.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Onboarding Form Information:</strong> Prior to scheduling a
            CFP® session, you will be asked to complete an onboarding form.
            This form requires financial information, including salary and
            expense data, to enable your CFP® professional to prepare for your
            session. The following fields are optional and may be left blank
            without any effect on your access to the Platform or any of its
            features: date of birth; demographic information, including age
            range and household size; gender; and racial or ethnic origin. Where
            your employer has elected to receive program-level reporting, some
            demographic information from onboarding responses may be shared with
            your employer on a strictly aggregate, anonymous basis. No personal
            financial information, and no individually identifiable information,
            is included in such reporting, and the data is presented at a level
            where individual identification is not possible. See Section 4(B)
            for the complete disclosure on employer data sharing.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>CFP® Session and View Access Information:</strong>{' '}
            Scheduling details, session topics, notes, and communication records
            associated with consultations with CFP® professionals made
            available through the Platform. Note that scheduling functionality
            may be facilitated through Calendly, Inc. (&ldquo;Calendly&rdquo;),
            a third-party scheduling tool; please review Calendly&rsquo;s
            privacy policy at{' '}
            <Anchor
              href="https://calendly.com/legal/privacy-notice"
              target="_blank"
              variant="primary"
            >
              https://calendly.com/legal/privacy-notice
            </Anchor>
            . When booking a CFP® appointment through Calendly, you may be
            asked to indicate the financial topics you wish to discuss. This
            information is collected by Calendly on our behalf and may be shared
            with Rolan Wealth to prepare your CFP® professional for your
            session. Additionally, if you voluntarily grant a CFP® professional
            &ldquo;View Access&rdquo; to your Platform account, that
            professional will have limited, read-only visibility into the
            following areas of your account: your dashboard, budget page, action
            plan pages, My Accounts page, and your onboarding questionnaire
            responses. CFP® View Access is granted solely to enable your CFP®
            professional to provide you with informed, personalized financial
            guidance based on your complete financial profile. CFP® View Access
            does not expose your linked financial account credentials, raw bank
            login data, or any ability to initiate transactions or interact with
            your financial institutions. You may revoke View Access at any time
            through your account settings, at which point the CFP®
            professional&rsquo;s access will be terminated immediately.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Identity Verification Information:</strong> We do not
            currently collect government-issued identification numbers or
            similar identity verification data. If we implement identity
            verification in the future, this Policy will be updated accordingly.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Voluntary Submissions:</strong> Profile photographs or
            images optionally uploaded to your account profile, survey
            responses, feedback, referrals, and other information you choose to
            provide. Profile image upload is optional; if uploaded, your profile
            image is stored on our servers and is visible within your own
            account only.
          </Text>
        </List.Item>
      </List>

      <Title mt={20} variant="h3">
        B. Information Collected Automatically
      </Title>

      <Text>
        When you access or use the Services, we automatically collect:
      </Text>

      <List
        classNames={{
          itemWrapper: classes.listItem,
        }}
        mt={8}
        withPadding
      >
        <List.Item>
          <Text>
            <strong>Log and Usage Data:</strong> IP address, browser type,
            operating system, timestamps, pages and features accessed,
            click-stream data, and system activity logs. We also log user
            sign-in and sign-out events to measure session duration and Platform
            engagement. This data is used solely for product analytics and
            Platform improvement and is not shared with your employer in
            individually identifiable form.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Device Data:</strong> Hardware model, device and application
            identifiers, mobile carrier, and system configuration information.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Cookies and Tracking Technologies:</strong> See Section 5
            (Cookies and Tracking Technologies) for details.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Error and Diagnostic Data:</strong> We use Sentry, an
            application error monitoring service provided by Functional
            Software, Inc. d/b/a Sentry (&ldquo;Sentry&rdquo;), to detect,
            diagnose, and resolve technical errors on the Platform&rsquo;s
            frontend. Sentry is configured to collect certain personally
            identifiable information as part of its frontend error reporting
            functionality, which may include IP addresses, device identifiers,
            user session identifiers, and event breadcrumbs (records of user
            actions and navigation steps preceding an error). This data is
            transmitted to and processed by Sentry&rsquo;s servers solely for
            error diagnosis and Platform quality assurance purposes.
            Sentry&rsquo;s data handling practices are governed by
            Sentry&rsquo;s Privacy Policy, available at{' '}
            <Anchor
              href="https://sentry.io/privacy/"
              target="_blank"
              variant="primary"
            >
              https://sentry.io/privacy/
            </Anchor>
            .
          </Text>
        </List.Item>
      </List>

      <Title mt={20} variant="h3">
        C. Information From Third Parties
      </Title>

      <Text>
        We may receive limited information from third parties, including:
      </Text>

      <List
        classNames={{
          itemWrapper: classes.listItem,
        }}
        mt={8}
        withPadding
      >
        <List.Item>
          <Text>
            <strong>Plaid, Inc.:</strong> When you connect your financial
            accounts through Plaid, account balance and transaction data is
            retrieved on-demand from your financial institutions via
            Plaid&rsquo;s API and displayed on the Platform. Rolan Wealth does
            not independently store raw transaction data, with one limited
            exception: where a user modifies a transaction on the Platform
            (e.g., recategorizing, renaming, or excluding a transaction), Rolan
            Wealth stores only the Plaid-issued transaction ID and the
            user&rsquo;s modification for reference. Underlying transaction
            details are not stored. Rolan Wealth also stores the Plaid access
            token (encrypted at rest using AES-256) to enable ongoing data
            retrieval. Rolan Wealth does not receive or store your bank login
            credentials. Your use of Plaid is subject to Plaid&rsquo;s End User
            Privacy Policy (
            <Anchor
              href="https://plaid.com/legal/end-user-privacy-policy"
              target="_blank"
              variant="primary"
            >
              https://plaid.com/legal/end-user-privacy-policy
            </Anchor>
            ).
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Identity Verification Providers:</strong> To the extent we
            engage identity verification or fraud detection services, those
            providers may receive data necessary to verify your identity. We
            will update this disclosure if and when such services are
            implemented.
          </Text>
        </List.Item>
      </List>

      <Text mt={20}>
        <strong>IMPORTANT:</strong> Rolan Wealth does not receive individually
        identifiable employee data from your employer in connection with your
        use of the Platform. Employers who contract with Rolan Wealth may
        confirm employee eligibility at an aggregate or roster level for
        benefits administration purposes only. Individual usage data, financial
        account data, CFP® session content, salary information, and personal
        financial information is never shared with your employer. Where an
        employer has elected to receive program-level reporting, some
        demographic information from onboarding responses may be shared on a
        strictly aggregate, anonymous basis at a level where individual
        identification is not possible. See Section 4(B) for the complete
        disclosure.
      </Text>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">2. How We Use Your Information</Title>

      <Text>
        We use personal information for the following purposes, each supported
        by a lawful basis under applicable law:
      </Text>

      <List
        classNames={{
          itemWrapper: classes.listItem,
        }}
        mt={8}
        withPadding
      >
        <List.Item>
          <Text>
            <strong>Service Delivery:</strong> To create and maintain your
            account, provide personalized financial wellness features, connect
            you with CFP® professionals, and process transactions.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>AI-Powered Insights Generation:</strong> To generate
            automated, rule-based informational observations, notifications, and
            summaries (&ldquo;AI-Powered Insights&rdquo;) derived from your
            financial profile and connected account data. AI-Powered Insights
            may be generated using rule-based logic and, in some cases,
            artificial intelligence or large language model technologies. These
            outputs are informational and educational in nature only and do not
            constitute financial, investment, legal, or tax advice. See our
            Terms and Conditions for the full disclaimer governing AI-Powered
            Insights.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Security and Fraud Prevention:</strong> To authenticate
            users, detect unauthorized access, prevent fraud, and protect the
            integrity of the Platform.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Analytics and Product Improvement:</strong> To understand
            usage patterns, diagnose technical issues, measure feature
            effectiveness, and develop new services. Analytics are performed on
            aggregated or de-identified data to the greatest extent practicable.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Communications:</strong> To send you transactional
            notifications, service updates, and&mdash;with your prior
            consent&mdash;marketing communications. You may opt out of marketing
            communications at any time (see Section 9).
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Legal and Regulatory Compliance:</strong> To fulfill our
            obligations under applicable federal, state, and local laws and
            regulations, respond to legal process, and cooperate with regulatory
            authorities.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Dispute Resolution and Enforcement:</strong> To enforce our
            Terms of Service and resolve disputes.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Aggregate Program Reporting:</strong> Where applicable, to
            produce aggregate, anonymous program-level reports for employer
            clients using demographic information (including gender and racial
            or ethnic origin where optionally provided) collected through the
            onboarding form. Such reports are used solely to evaluate and
            improve the quality and relevance of financial wellness programming.
            Demographic data, including gender and racial or ethnic origin, is
            never used for individual profiling, is never used to make any
            decision about an individual user&rsquo;s access to or experience of
            the Platform or its features, and is never shared with any third
            party in individually identifiable form.
          </Text>
        </List.Item>
      </List>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">
        3. Restrictions on Use of Your Personal Information
      </Title>

      <Text>We want to be explicit about activities we do not engage in:</Text>

      <List
        classNames={{
          itemWrapper: classes.listItem,
        }}
        mt={8}
        withPadding
      >
        <List.Item>
          <Text>
            Rolan Wealth does not sell, rent, or trade your personal information
            to third parties for their independent use, including for targeted
            advertising purposes. &ldquo;Sell&rdquo; has the meaning given to it
            under applicable state privacy laws, including the California
            Consumer Privacy Act (CCPA).
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            Rolan Wealth does not share individually identifiable employee
            personal information or financial data with your employer or any
            affiliated entity of your employer.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            Rolan Wealth does not use your personal information to make
            automated decisions that produce legal or similarly significant
            effects without human review and appropriate disclosures.
          </Text>
        </List.Item>
      </List>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">4. How We Share Your Information</Title>

      <Text>
        We share personal information only in the limited circumstances
        described below:
      </Text>

      <Title mt={20} variant="h3">
        A. Service Providers
      </Title>

      <Text>
        We engage third-party vendors and service providers who process personal
        information on our behalf to support Platform operations. These
        providers are contractually required to: (i) use personal information
        solely as directed by us; (ii) implement appropriate technical and
        organizational security measures; and (iii) not disclose personal
        information to additional parties without our authorization.
      </Text>

      <Text mt={8}>
        Current categories of service providers include: cloud hosting and
        infrastructure providers; financial data aggregation services (Plaid);
        scheduling services (Calendly); analytics providers (Google Analytics);
        error monitoring services (Sentry); pilot enrollment and form services
        (Google LLC); transactional email services (SendGrid, provided by Twilio
        Inc.); and communications platforms (SMS and other communications).
      </Text>

      <Text mt={8}>
        For certain employer pilot programs, initial employee enrollment may be
        facilitated through a Google Form hosted by Google LLC. Information
        submitted through that form (such as your work email address) is
        processed by Google in accordance with Google&rsquo;s Privacy Policy (
        <Anchor
          href="https://policies.google.com/privacy"
          target="_blank"
          variant="primary"
        >
          https://policies.google.com/privacy
        </Anchor>
        ) and Google Workspace data processing terms. Rolan Wealth uses this
        information solely to provision access to the Platform for eligible
        participants.
      </Text>

      <Title mt={20} variant="h3">
        B. Employer Clients &mdash; Aggregate Data Only
      </Title>

      <Text>
        Where your employer has elected to receive program-level reporting, we
        may provide aggregated, de-identified reports about platform engagement
        and financial wellness trends across their employee population. Such
        reports may include high-level demographic summaries derived from
        onboarding form responses, presented at a level where individual
        identification is not possible. No personal financial information,
        individual usage data, CFP® session content, salary information, or any
        personally identifiable information is ever shared with your employer.
      </Text>

      <Title mt={20} variant="h3">
        C. Legal Requirements and Protection of Rights
      </Title>

      <Text>
        We may disclose personal information if required to do so by law or in
        the good-faith belief that such disclosure is reasonably necessary to:
      </Text>

      <List
        classNames={{
          itemWrapper: classes.listItem,
        }}
        mt={8}
        withPadding
      >
        <List.Item>
          <Text>
            Comply with a legal obligation, court order, subpoena, or valid
            legal process;
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            Protect the rights, property, or safety of Rolan Wealth, our users,
            or the public;
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            Detect, prevent, or address fraud, security, or technical issues; or
          </Text>
        </List.Item>

        <List.Item>
          <Text>Enforce our Terms of Service or other agreements.</Text>
        </List.Item>
      </List>

      <Title mt={20} variant="h3">
        D. Business Transfers
      </Title>

      <Text>
        In the event of a merger, acquisition, reorganization, bankruptcy, or
        sale of all or a portion of our assets, personal information may be
        transferred to the acquiring entity as part of such transaction. We will
        provide notice via email or a prominent notice on the Platform prior to
        personal information becoming subject to a materially different privacy
        policy, and will afford users the opportunity to exercise applicable
        rights.
      </Text>

      <Title mt={20} variant="h3">
        E. With Your Consent
      </Title>

      <Text>
        We may share your personal information with third parties not described
        above when you provide explicit consent to do so.
      </Text>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">5. Cookies and Tracking Technologies</Title>

      <Text>
        We use cookies, web beacons, and similar technologies to support
        Platform functionality and gather analytics. This section describes the
        cookies currently in use and your options for managing them.
      </Text>

      <List
        classNames={{
          itemWrapper: classes.listItem,
        }}
        mt={8}
        withPadding
      >
        <List.Item>
          <Text>
            <strong>First-Party Functional Cookies:</strong> Rolan Wealth uses
            first-party cookies that are necessary for the Platform to operate.
            These include session cookies (which expire when you close your
            browser) used for authentication and secure session management, and
            persistent cookies used to remember your login state and user
            preferences. These cookies are essential to the functioning of the
            Platform and cannot be disabled without impairing your ability to
            use the Services.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Third-Party Cookies:</strong> Third-party services
            integrated into the Platform, including Plaid (financial account
            connectivity) and analytics tools such as Google Analytics, may set
            their own cookies on your device when you interact with their
            features. Rolan Wealth does not control these cookies. Plaid&rsquo;s
            cookie and tracking practices are governed by Plaid&rsquo;s Privacy
            Policy (
            <Anchor
              href="https://plaid.com/legal/privacy-policy"
              target="_blank"
              variant="primary"
            >
              https://plaid.com/legal/privacy-policy
            </Anchor>
            ). Google Analytics&rsquo; practices are governed by Google&rsquo;s
            Privacy Policy (
            <Anchor
              href="https://policies.google.com/privacy"
              target="_blank"
              variant="primary"
            >
              https://policies.google.com/privacy
            </Anchor>
            ).
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Web Beacons and Email Tracking:</strong> Transactional
            emails sent by Rolan Wealth (such as account signup confirmations
            and password reset emails) are delivered via SendGrid, a
            transactional email service provided by Twilio Inc. SendGrid&rsquo;s
            default configuration may embed tracking pixels (web beacons) in
            outgoing emails that record whether an email was opened and whether
            any links were clicked. This data is used solely to confirm delivery
            and diagnose technical issues with email delivery &mdash; not for
            marketing or profiling purposes. If you wish to prevent this
            tracking, you may disable automatic image loading in your email
            client settings, which will prevent tracking pixels from firing.
            SendGrid&rsquo;s data practices are governed by Twilio&rsquo;s
            Privacy Policy, available at{' '}
            <Anchor
              href="https://www.twilio.com/en-us/legal/privacy"
              target="_blank"
              variant="primary"
            >
              https://www.twilio.com/en-us/legal/privacy
            </Anchor>
            .
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Cookie Consent Banner:</strong> Rolan Wealth does not
            currently operate a cookie consent banner or preference management
            tool. We are working to implement a cookie consent mechanism as part
            of our ongoing compliance development. In the meantime, you may
            manage or refuse cookies through your browser settings at any time.
            Note that disabling essential first-party cookies will impair your
            ability to log in and use the Platform. For instructions on managing
            cookies in your browser, visit{' '}
            <Anchor
              href="https://www.allaboutcookies.org"
              target="_blank"
              variant="primary"
            >
              https://www.allaboutcookies.org
            </Anchor>
            .
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Google Analytics Opt-Out:</strong> To opt out of Google
            Analytics tracking across all websites, you may install the Google
            Analytics Opt-Out Browser Add-on available at{' '}
            <Anchor
              href="https://tools.google.com/dlpage/gaoptout"
              target="_blank"
              variant="primary"
            >
              https://tools.google.com/dlpage/gaoptout
            </Anchor>
            .
          </Text>
        </List.Item>
      </List>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">6. Data Security</Title>

      <Text>
        We implement administrative, technical, and physical safeguards designed
        to protect personal information against unauthorized access, disclosure,
        alteration, and destruction. The following describes our current
        security posture. Where a measure is described as planned or in
        development, it is not yet operational.
      </Text>

      <List
        classNames={{
          itemWrapper: classes.listItem,
        }}
        mt={8}
        withPadding
      >
        <List.Item>
          <Text>
            <strong>Encryption in Transit:</strong> All communications between
            users and the Platform, and between the Platform and third-party
            data providers (including Plaid), are encrypted using TLS (Transport
            Layer Security).
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Encryption at Rest:</strong> All sensitive data stored by
            Rolan Wealth is encrypted at rest using AES-256 encryption. This
            includes Plaid access tokens, Plaid-connected account metadata,
            manually entered account data (balances, institution details, and
            all fields described in Section 1(A)), user-modified transaction
            records, onboarding form data, and messages exchanged between users
            and CFP® professionals through the Platform&rsquo;s messaging
            feature. In the event of an unauthorized database access, stored
            data cannot be decoded without the encryption key.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Role-Based Access Controls (RBAC):</strong> Access to
            personal information is restricted to authorized personnel based on
            role and business need, including strict IAM (Identity and Access
            Management) controls on cloud infrastructure functions.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Security Monitoring:</strong> We use real-time monitoring
            tools, including AWS CloudWatch and Cloudflare, to detect suspicious
            activity and potential security incidents. We conduct regular
            vulnerability scans and audit IAM roles and permissions. DDoS
            protection is provided via Cloudflare.
          </Text>
        </List.Item>
      </List>

      <Text mt={20}>
        No method of transmission over the Internet or electronic storage system
        is completely secure. We cannot guarantee absolute security of your
        information, and you bear responsibility for maintaining the
        confidentiality of your account credentials. You should notify us
        immediately at privacy@rolanwealth.com if you suspect any unauthorized
        access to or use of your account.
      </Text>

      <Text mt={8}>
        <strong>Security Development:</strong> We continuously evaluate and
        improve our security posture. Planned enhancements to our Platform
        security include additional account authentication controls and
        anomalous login detection. We will update this Privacy Policy to reflect
        material changes to our security practices as they are implemented.
      </Text>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">7. Data Retention</Title>

      <Text>
        We retain personal information for as long as your account remains
        active and for a period thereafter as necessary to fulfill the purposes
        described in this Privacy Policy or to comply with applicable legal
        obligations. Specifically:
      </Text>

      <List
        classNames={{
          itemWrapper: classes.listItem,
        }}
        mt={8}
        withPadding
      >
        <List.Item>
          <Text>
            <strong>Active Accounts:</strong> Personal information is retained
            for the duration of your active account.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Account Deletion:</strong> When you delete your account
            &mdash; whether through the in-app deletion feature or by written
            request to privacy@rolanwealth.com &mdash; all of your personal data
            is deleted immediately from our active database. This includes your
            profile information (name, email addresses, phone number, and
            profile image), manually entered account data, Plaid access tokens
            and account metadata, onboarding form responses, user-modified
            transaction records, and messages exchanged with CFP®
            professionals. No active retention period applies following account
            deletion. Residual copies in automated backup systems are purged in
            the ordinary course of backup rotation. If you have a legal basis to
            require earlier backup deletion, please contact us.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Linked Financial Account Data and Tokens:</strong> Plaid
            access tokens, Plaid-connected account metadata (account name,
            institution, last four digits), and user-modified transaction
            records are all deleted immediately upon account deletion as part of
            the active database purge described above. No separate retention
            period applies to this data.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Aggregate and De-Identified Data:</strong> We may retain and
            use de-identified or aggregated data derived from your information
            indefinitely, as such data is no longer personal information.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Legal Holds:</strong> Where required by law, regulation, or
            valid legal process, we may be obligated to retain certain
            information beyond the periods described above.
          </Text>
        </List.Item>
      </List>

      <Text mt={20}>
        To delete your personal information, use the account deletion feature
        within the Platform, which deletes all of your data immediately from our
        active database. Alternatively, submit a written deletion request to
        privacy@rolanwealth.com and we will process it promptly. Where a written
        request requires identity verification before processing, we will
        complete deletion within forty-five (45) days of verification. Deletion
        from automated backup systems occurs in the ordinary course of backup
        rotation. Legal holds, if any, are described in the bullet above.
      </Text>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">8. Data Breach Notification</Title>

      <Text>
        In the event of a security breach that compromises personal information,
        we will notify affected individuals promptly and in accordance with the
        requirements of applicable federal and state data breach notification
        laws. Notifications will be provided via email to the address associated
        with your account and/or through conspicuous notice on the Platform. We
        will describe the nature of the breach, the information affected, and
        the steps we are taking to address it.
      </Text>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">9. Your Privacy Rights</Title>

      <Text>
        Depending on your jurisdiction and applicable law, you may have one or
        more of the following rights with respect to your personal information:
      </Text>

      <List
        classNames={{
          itemWrapper: classes.listItem,
        }}
        mt={8}
        withPadding
      >
        <List.Item>
          <Text>
            <strong>Right to Know / Access:</strong> The right to request
            disclosure of the categories and specific pieces of personal
            information we have collected about you, the sources of collection,
            the purposes for which it is used, and the categories of third
            parties with whom it is shared.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Right to Correct:</strong> The right to request correction
            of inaccurate personal information we maintain about you.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Right to Delete:</strong> The right to request deletion of
            personal information we have collected about you, subject to certain
            legal exceptions.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Right to Opt Out of Sale / Sharing:</strong> The right to
            opt out of the sale of your personal information or its sharing for
            targeted advertising purposes. As stated in Section 3, Rolan Wealth
            does not sell personal information. If our practices change, we will
            update this Policy and provide a clear opt-out mechanism.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Right to Non-Discrimination:</strong> You have the right not
            to receive discriminatory treatment for exercising any privacy
            rights described in this Policy.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Right to Data Portability:</strong> In certain
            jurisdictions, the right to receive a copy of your personal
            information in a structured, commonly used, machine-readable format.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Right to Restrict Processing / Object:</strong> In certain
            jurisdictions, the right to restrict or object to our processing of
            your personal information under certain circumstances.
          </Text>
        </List.Item>
      </List>

      <Text mt={20}>
        To exercise any of these rights, please submit a request to
        privacy@rolanwealth.com. We will verify your identity before processing
        your request and will respond within the timeframe required by
        applicable law (typically 45 days, with a possible extension of an
        additional 45 days upon notice to you). We will not charge a fee for
        responding to your request unless it is excessive, repetitive, or
        manifestly unfounded.
      </Text>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">10. California Privacy Rights (CCPA/CPRA)</Title>

      <Text>
        This section applies to California residents and supplements the rest of
        this Privacy Policy. It is provided pursuant to the California Consumer
        Privacy Act of 2018, as amended by the California Privacy Rights Act of
        2020 (collectively, &ldquo;CCPA&rdquo;).
      </Text>

      <Title mt={20} variant="h3">
        A. Categories of Personal Information Collected
      </Title>

      <Text>
        In the twelve (12) months preceding the effective date of this Policy,
        we have collected the following categories of personal information about
        California residents, as defined by the CCPA:
      </Text>

      <List
        classNames={{
          itemWrapper: classes.listItem,
        }}
        mt={8}
        withPadding
      >
        <List.Item>
          <Text>
            <strong>Identifiers:</strong> Name, work email address, personal
            email address (if optionally provided), phone number (if optionally
            provided), IP address, account credentials, and device identifiers.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Personal Information under Cal. Civ. Code §1798.80:</strong>{' '}
            Name, email address(es), phone number (if optionally provided), date
            of birth, and financial account information.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Sensitive Personal Information:</strong> Precise financial
            data including self-reported income, account balances retrieved via
            Plaid, and user-modified transaction records; the contents of CFP®
            session communications; and, where optionally provided through the
            onboarding form, racial or ethnic origin and gender.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Internet or Electronic Network Activity:</strong> Browsing
            history on the Platform, page interactions, feature usage, and
            clickstream data.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Geolocation Data:</strong> Approximate geographic location
            derived from IP address. We do not collect precise GPS-based
            location data.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Inferences:</strong> Profiles derived from financial data to
            generate personalized wellness insights and recommendations.
          </Text>
        </List.Item>
      </List>

      <Title mt={20} variant="h3">
        B. Purposes of Collection and Use
      </Title>

      <Text>
        We collect and use the above categories of personal information for the
        business and commercial purposes described in Section 2 of this Privacy
        Policy, including service delivery, security, analytics, communications,
        and legal compliance.
      </Text>

      <Title mt={20} variant="h3">
        C. Disclosure of Personal Information
      </Title>

      <Text>
        We disclose the above categories of personal information to the
        categories of third parties described in Section 4 of this Privacy
        Policy (service providers, legal authorities, and successors in
        interest), solely for the business purposes described herein.
      </Text>

      <Title mt={20} variant="h3">
        D. No Sale or Sharing of Personal Information
      </Title>

      <Text>
        <strong>
          Rolan Wealth does not sell California residents&rsquo; personal
          information for monetary or other valuable consideration, and does not
          share personal information with third parties for cross-context
          behavioral advertising purposes, as those terms are defined under the
          CCPA.
        </strong>
      </Text>

      <Title mt={20} variant="h3">
        E. Sensitive Personal Information
      </Title>

      <Text>
        To the extent we collect sensitive personal information as defined by
        the CCPA, we use it solely for purposes reasonably necessary to provide
        the Services. We do not use or disclose sensitive personal information
        for purposes beyond those permitted by the CCPA without your consent.
      </Text>

      <Title mt={20} variant="h3">
        F. California Consumer Rights
      </Title>

      <Text>
        California residents have the following specific rights under the CCPA,
        in addition to those described in Section 9:
      </Text>

      <List
        classNames={{
          itemWrapper: classes.listItem,
        }}
        mt={8}
        withPadding
      >
        <List.Item>
          <Text>
            <strong>Right to Know:</strong> You may request that we disclose (i)
            the categories of personal information collected; (ii) the
            categories of sources; (iii) the business or commercial purposes for
            collection; (iv) the categories of third parties with whom personal
            information is shared; and (v) the specific pieces of personal
            information collected about you.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Right to Delete:</strong> You may request deletion of
            personal information we have collected about you, subject to certain
            exceptions (e.g., where retention is required to complete a
            transaction, detect security incidents, or comply with a legal
            obligation).
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Right to Correct:</strong> You may request correction of
            inaccurate personal information.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Right to Opt Out of Sale/Sharing:</strong> As noted above,
            we do not sell or share personal information for behavioral
            advertising. No opt-out mechanism is required at this time; however,
            if our practices change, we will provide a &ldquo;Do Not Sell or
            Share My Personal Information&rdquo; link on our Platform.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>
              Right to Limit Use of Sensitive Personal Information:
            </strong>{' '}
            You may request that we limit our use of your sensitive personal
            information &mdash; which for Rolan Wealth consists of precise
            financial data, CFP® session communication contents, and, where
            optionally provided, racial or ethnic origin and gender &mdash; to
            uses reasonably necessary for providing the Services, as permitted
            by the CCPA/CPRA. With respect to the optional sensitive PI fields
            (racial or ethnic origin and gender), you may exercise control at
            the point of collection by simply declining to complete those fields
            in the onboarding form. Doing so will not affect your access to the
            Platform or any of its features.
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            <strong>Right to Non-Discrimination:</strong> We will not
            discriminate against you for exercising your CCPA rights, including
            by denying services, charging different prices, or providing a
            different level of service quality.
          </Text>
        </List.Item>
      </List>

      <Title mt={20} variant="h3">
        G. Submitting Requests
      </Title>

      <Text>
        California residents may submit requests to exercise their CCPA rights
        by:
      </Text>

      <List
        classNames={{
          itemWrapper: classes.listItem,
        }}
        mt={8}
        withPadding
      >
        <List.Item>
          <Text>
            <strong>Email:</strong> privacy@rolanwealth.com (subject line:
            &ldquo;California Privacy Request&rdquo;)
          </Text>
        </List.Item>
      </List>

      <Text mt={8}>
        We will acknowledge receipt of your request within ten (10) business
        days and respond within forty-five (45) calendar days. We may extend
        this period by an additional forty-five (45) days when reasonably
        necessary, with prior notice. We will verify your identity by matching
        information you provide against information in our records.
      </Text>

      <Text mt={8}>
        <strong>Authorized Agents:</strong> You may designate an authorized
        agent to submit a request on your behalf. We may require you to directly
        verify your identity with us and confirm the agent&rsquo;s
        authorization.
      </Text>

      <Title mt={20} variant="h3">
        H. Shine the Light
      </Title>

      <Text>
        California Civil Code §1798.83 (&ldquo;Shine the Light&rdquo;) permits
        California residents to request, once per calendar year, information
        about the personal information we disclose to third parties for direct
        marketing purposes. Rolan Wealth does not disclose personal information
        to third parties for direct marketing purposes without prior consent.
      </Text>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">
        11. State Privacy Rights &mdash; Other Jurisdictions
      </Title>

      <Text>
        Users in certain states may have additional rights under applicable
        state privacy laws, including but not limited to residents of Colorado
        (Colorado Privacy Act), Connecticut (Connecticut Data Privacy Act),
        Virginia (Virginia Consumer Data Protection Act), Texas (Texas Data
        Privacy and Security Act), and other states with enacted consumer
        privacy legislation. These rights generally parallel those described in
        Sections 9 and 10 above, including rights to access, correct, delete,
        opt out of sale/sharing, and opt out of profiling. To exercise
        applicable state privacy rights, please contact privacy@rolanwealth.com.
      </Text>

      <Title mt={20} variant="h3">
        Note on GDPR and International Privacy Laws
      </Title>

      <Text>
        Rolan Wealth is a U.S.-based platform that currently provides Services
        exclusively to U.S.-based employees of U.S.-based employers. We do not
        direct our Services to individuals in the European Union, United
        Kingdom, or other jurisdictions subject to the General Data Protection
        Regulation (GDPR) or equivalent international data protection laws.
        Accordingly, GDPR does not currently apply to our operations. If Rolan
        Wealth expands its Services to users in jurisdictions covered by GDPR or
        similar regulations in the future, we will update this Privacy Policy to
        reflect the applicable legal requirements at that time.
      </Text>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">12. Children&rsquo;s Privacy</Title>

      <Text>
        The Services are intended for use by individuals who are at least
        eighteen (18) years of age. We do not knowingly collect, solicit, or use
        personal information from individuals under the age of 18. If we learn
        that we have inadvertently collected personal information from a minor,
        we will take prompt steps to delete such information. If you believe we
        may have collected personal information from a minor, please contact us
        at privacy@rolanwealth.com.
      </Text>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">13. Third-Party Links and Services</Title>

      <Text>
        The Platform may contain links to third-party websites, applications, or
        services not operated or controlled by Rolan Wealth. This Privacy Policy
        does not apply to third-party services. We encourage you to review the
        privacy policies of any third-party service you access through or in
        connection with our Platform. We are not responsible for the privacy
        practices of any third party.
      </Text>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">14. Changes to This Privacy Policy</Title>

      <Text>
        We may update this Privacy Policy from time to time to reflect changes
        to our practices, technology, legal requirements, or for other
        operational or business reasons. When we make material changes, we will:
      </Text>

      <List
        classNames={{
          itemWrapper: classes.listItem,
        }}
        mt={8}
        withPadding
      >
        <List.Item>
          <Text>
            Update the &ldquo;Last Updated&rdquo; date at the top of this
            Policy;
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            Notify you via email to the address associated with your account;
            and/or
          </Text>
        </List.Item>

        <List.Item>
          <Text>
            Post a conspicuous notice on the Platform or its login page.
          </Text>
        </List.Item>
      </List>

      <Text mt={8}>
        Your continued use of the Services after the effective date of an
        updated Privacy Policy constitutes your acceptance of the changes to the
        extent permitted by applicable law. We encourage you to review this
        Privacy Policy periodically.
      </Text>

      <Divider classNames={{ root: classes.separator }} />

      <Title variant="h2">15. Contact Information</Title>

      <Text>
        For questions, concerns, or requests relating to this Privacy Policy or
        our privacy practices, or to exercise any of your rights described
        herein, please contact us at:
      </Text>

      <Text mt={8}>
        <strong>Rolan Wealth (CLZ Ventures LLC)</strong>
      </Text>

      <Text>Attn: Privacy Officer</Text>

      <Text>54 State Street, Suite 804 #7100</Text>

      <Text>Albany, New York 12207</Text>

      <Text>Email: privacy@rolanwealth.com</Text>

      <Text mt={20}>
        We will acknowledge receipt of your inquiry within five (5) business
        days and will make commercially reasonable efforts to resolve your
        concern or respond to your request in a timely manner.
      </Text>

      <Text mt={20}>
        <em>
          Rolan Wealth is a service of CLZ Ventures LLC © 2026. All rights
          reserved.
        </em>
      </Text>

      <Text mt={8}>
        <em>
          CFP® and CERTIFIED FINANCIAL PLANNER&trade; are certification marks
          owned by Certified Financial Planner Board of Standards, Inc.
        </em>
      </Text>
    </Box>
  )
}

Component.displayName = 'PrivacyPolicy'
